// ==========================================================================
// JavaScript Component
// Author: Globalia
// Licensed under the WTFPL license
// ==========================================================================

import {
    toggleAttributeValue,
    removeAttributeValue
} from 'groots/utilities';

class GrHubspotSearch {
    constructor(selector) {
        this.pluginName = 'grHubspotSearch';

        // Create global references
        this.selector = typeof selector === 'string' ? document.querySelector(selector) : selector;

        // Set default parameters
        this.option = true;

        // Retrieve plugin custom parameters
        this.param = this.selector.getAttribute('gr-hubspot-search').split(' ');

        this.params = this.urlParams('term');
        this.lang = document.documentElement.lang;
        this.template = this.selector.querySelector('.hs-search');
        this.prevLink = this.selector.querySelector('.hs-search__prev');
        this.totalContainer = document.querySelector('.hs-search__total');
        this.nextLink = this.selector.querySelector('.hs-search__next');
        this.pagination = this.selector.querySelector('.hs-search__pagination');
        this.container = this.selector.querySelector('.hs-search__container');
        this.searchPath = this.selector.querySelector('.hs-search__pagination').getAttribute('data-search-path');

        // Overwrite with custom parameters
        for (let index = 0; index < this.param.length; index++) {
            const attr = this.param[index];
            this.target = attr;
        }

        // Init plugin
        this.init();
    }

    //  Methods
    // ----------------------------------------------------------------------

    init() {
        if (this.selector === null) {
            throw new Error(this.pluginName + ': fail - something wrong with your selector');
        } else {
            // Bind all event handlers for referencability
            var arr = [];

            for (var i = 0, len = arr.length; i < len; i++) {
                var method = arr[i];
                this[method] = this[method].bind(this);
            }

            // Flag the plugin as ready
            this.ready = true;
            toggleAttributeValue(this.selector, 'gr-hubspot-search', 'ready');

            this.process();

            if (window.groots.verbose) {
                console.warn(this.pluginName + '(' + this.selector.id + '): ready');
            }
        }
    }

    process() {
        if (window.groots.verbose) {
            console.warn(this.pluginName + ': processing start');
        }

        toggleAttributeValue(this.selector, 'gr-hubspot-search', 'processing');

        this.httpRequest();

        removeAttributeValue(this.selector, 'gr-hubspot-search', 'processing');

        if (window.groots.verbose) {
            console.warn(this.pluginName + ': processed');
        }
    }

    urlParams(name) {
        // eslint-disable-next-line
        const results = new RegExp('[\?&]' + name + '=([^&#]*)').exec(window.location.href);

        if (results === null){
            return null;
        } else{
            return decodeURI(results[1]) || 0;
        }
    }

    getTerm() {
        return encodeURI(this.urlParams('term')) || '';
    }

    getOffset() {
        return parseInt(this.urlParams('offset')) || 0;
    }

    getLimit() {
        return parseInt(this.urlParams('limit'));
    }

    addResult(title, url, tags, publishedDate, featuredImage) {
        const result = this.template.cloneNode(true);

        var post_date = new Date(publishedDate);

        var day = post_date.getDate();
        var monthIndex = post_date.getMonth();
        var year = post_date.getFullYear();

        var monthNames_en = [
            'january', 'february', 'march',
            'april', 'may', 'june', 'july',
            'august', 'september', 'october',
            'november', 'december',
        ];

        var monthNames_fr = [
            'janvier', 'février', 'mars',
            'avril', 'mai', 'juin', 'juillet',
            'août', 'septembre', 'octobre',
            'novembre', 'decembre',
        ];

        var month;

        if (this.lang === 'en') {
            month = monthNames_en[monthIndex];
        } else if (this.lang === 'fr') {
            month = monthNames_fr[monthIndex];
        }

        result.querySelector('.hs-search__title').innerHTML = title;
        result.querySelector('.hs-search__link').href = url;
        result.querySelector('.listing-card__media').setAttribute('style', 'background-image: url(' + featuredImage + ')');
        result.querySelector('.article--category-link').innerHTML = tags[0];
        result.querySelector('.hs-date').innerHTML = day + ' ' + month + ' ' + year;
        this.container.appendChild(result);
    }

    fillResults(results) {
        const that = this;
        results.results.forEach(function(result) {
            that.addResult(result.title, result.url, result.tags, result.publishedDate, result.featuredImageUrl);
        });
    }

    emptyResults(searchedTerm) {
        if (this.lang === 'en') {
            this.container.innerHTML = '<div class="u-text-align-center hs-search__no-results"><p>Sorry. There are no results for <strong>' + searchedTerm + '</strong></p>' +
            '<p>Try rewording your query, or browse through our site.</p></div>';
        } else if (this.lang === 'fr') {
            this.container.innerHTML = '<div class="u-text-align-center hs-search__no-results"><p>Pardon. Il n\'y a pas de résultats pour <strong>' + searchedTerm + '</strong></p>' +
                '<p>Essayez de reformuler votre requête ou parcourez notre site.</p></div>';
        }
    }

    setSearchBarDefault(searchedTerm) {
        const searchBars = document.querySelectorAll('.form-search__input');

        Array.prototype.forEach.call(searchBars, function(el) {
            el.value = decodeURIComponent(searchedTerm);
        });
    }

    paginate(results) {
        const updatedLimit = this.getLimit() || results.limit;

        this.totalContainer.innerHTML = results.total;

        function hasPreviousPage() {
            return results.page > 0;
        }
        function hasNextPage() {
            return results.offset < (results.total - updatedLimit);
        }

        if (hasPreviousPage()) {
            const urlPrev = '/' + this.searchPath + '?term=' + this.getTerm() + '&pathPrefix=%2F' + this.lang + '&offset=' + ((results.page * updatedLimit) - parseInt(updatedLimit));
            this.prevLink.href = urlPrev;
        } else {
            this.prevLink.classList.add('pagination__link--disabled');
        }

        if (hasNextPage()) {
            const urlNext = '/' + this.searchPath + '?term=' + this.getTerm() + '&pathPrefix=%2F' + this.lang + '&offset=' + ((results.page * updatedLimit) + parseInt(updatedLimit));
            this.nextLink.href = urlNext;
        } else {
            this.nextLink.classList.add('pagination__link--disabled');
        }
    }

    httpRequest() {
        const SEARCH_URL = '/_hcms/search/',
            requestUrl = SEARCH_URL + '?term=' + this.getTerm() + '&pathPrefix=%2F' + this.lang  + '&offset=' + this.getOffset() + '&analytics=false&limit=12&minScore=0.01',
            request = new XMLHttpRequest(),
            that = this;

        request.open('GET', requestUrl, true);

        request.onload = function() {
            if (request.status >= 200 && request.status < 400) {
                var data = JSON.parse(request.responseText);
                //this.setSearchBarDefault(data.searchTerm);

                if (data.total > 0) {
                    that.fillResults(data);
                    that.paginate(data);
                    that.pagination.classList.add('is-active');
                } else {
                    that.emptyResults(data.searchTerm);
                }
            } else {
                console.error('Server reached, error retrieving results.');
            }

            document.querySelector('.hs-search-results').classList.remove('is-loading');
        };

        request.onerror = function() {
            console.error('Could not reach the server.');
        };

        request.send();
    }


    // Destroy - remove listeners to prevent from memory leak
    destroy() {
        removeAttributeValue(this.selector, 'gr-hubspot-search', 'ready processing');

        if (window.groots.verbose) {
            console.warn(this.pluginName + '(' + this.selector.id + '): destroyed');
        }
    }
}

export default GrHubspotSearch;

//  Auto-Instantiation
// ----------------------------------------------------------------------

(function() {
    const all = document.querySelectorAll('[gr-hubspot-search]');
    window.groots.plugins.grComponent = {};

    if (all.length) {
        window.groots.plugins.grComponent.instances = {};
        let index = -1;

        while (all[++index]) {
            window.groots.plugins.grComponent.instances[index] = new GrHubspotSearch(all[index]);
        }
    }
}());
